import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import UploadComponent from "./UploadComponent";
import { useTranslation } from 'react-i18next';

function App() {
  const { instance, accounts } = useMsal();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [checkedAuth, setCheckedAuth] = useState(false);
  const { t, i18n } = useTranslation();

  const handleLogin = () => instance.loginRedirect();
  const handleLogout = () => instance.logoutRedirect();
  const isAuthenticated = accounts.length > 0;

  const groupIdAllowed = "19c04690-3c74-49e1-bf64-b5f20a5ebe21";

  useEffect(() => {
    const checkAuthorization = () => {
      if (!isAuthenticated) return;

      try {
        const claims = accounts[0]?.idTokenClaims;
        const userRoles = claims?.roles || [];
        const hasUploaderRole = userRoles.includes(groupIdAllowed);
        console.log("User claims:", claims);
        setIsAuthorized(hasUploaderRole);
      } catch (err) {
        console.error("Authorization check failed:", err);
      } finally {
        setCheckedAuth(true);
      }
    };

    checkAuthorization();
  }, [accounts, isAuthenticated]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  return (
    <div
      style={{
        backgroundColor: "#f5f7fa",
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        position: "relative", // Crucial for positioning the Technion logo absolutely
        padding: "20px", // Add padding to ensure content doesn't touch edges
      }}
      dir={i18n.language === 'he' ? 'rtl' : 'ltr'}
    >
      {/* Technion Logo in the top right corner */}
      <img
        src="/technion_logo.png" // Make sure you've renamed image_c6bff4.png to technion_logo.png and placed it in the public folder
        alt="Technion - Israel Institute of Technology"
        style={{
          position: "absolute",
          top: "20px",    // Distance from the top
          right: "20px",  // Distance from the right
          maxWidth: "150px", // Adjust size as needed
          zIndex: 10,     // Ensure it's above other content
        }}
      />

      {/* Main content container (the white box) */}
      <div
        style={{
          backgroundColor: "#fff",
          padding: "2.5em",
          borderRadius: "16px",
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
          textAlign: "center",
          width: "90%",
          maxWidth: "600px",
          position: "relative", // Ensure relative positioning for internal elements if any
          zIndex: 5, // Keep main content below the Technion logo if they overlap
        }}
      >
        {/* Your existing Civil & Environmental Engineering logo */}
        <img
          src="/logo.png" // This is your existing logo (Civil & Environmental Engineering)
          alt="Civil & Environmental Engineering"
          style={{ maxWidth: "180px", marginBottom: "1.5em" }}
        />

        <h2 style={{ fontSize: "1.8em", fontWeight: "bold", marginBottom: "1em", color: "#002855" }}>
          {t('upload_files_title')}
        </h2>

        <div style={{ marginBottom: '1.5em' }}>
          <button
            onClick={() => changeLanguage('en')}
            style={{
              marginRight: '12px',
              padding: '10px 18px',
              cursor: 'pointer',
              backgroundColor: i18n.language === 'en' ? '#007bff' : '#e9ecef',
              color: i18n.language === 'en' ? 'white' : '#495057',
              border: '1px solid #ced4da',
              borderRadius: '6px',
              transition: 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease'
            }}
          >
            English
          </button>
          <button
            onClick={() => changeLanguage('he')}
            style={{
              padding: '10px 18px',
              cursor: 'pointer',
              backgroundColor: i18n.language === 'he' ? '#007bff' : '#e9ecef',
              color: i18n.language === 'he' ? 'white' : '#495057',
              border: '1px solid #ced4da',
              borderRadius: '6px',
              transition: 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease'
            }}
          >
            עברית
          </button>
        </div>

        {!isAuthenticated ? (
          <button
            onClick={handleLogin}
            style={{
              padding: "12px 25px",
              backgroundColor: "#0038a8",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              fontSize: "1.05em",
              fontWeight: "bold",
              cursor: "pointer",
              marginTop: "1em",
              boxShadow: "0 4px 8px rgba(0, 56, 168, 0.2)",
              transition: "background-color 0.3s ease, box-shadow 0.3s ease, transform 0.2s ease"
            }}
          >
            {t('login_button')}
          </button>
        ) : !checkedAuth ? (
          <p style={{ color: "#555", fontSize: "1em" }}>{t('checking_permissions_message')}</p>
        ) : !isAuthorized ? (
          <>
            <p style={{ color: "#dc3545", fontWeight: "bold", marginBottom: "1.5em", fontSize: "1.1em" }}>
              {t('not_authorized_message')}
            </p>
            <button
              onClick={handleLogout}
              style={{
                padding: "10px 20px",
                backgroundColor: "#6c757d",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                fontSize: "0.95em",
                cursor: "pointer",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                transition: "background-color 0.3s ease, box-shadow 0.3s ease"
              }}>
              {t('logout_button')}
            </button>
          </>
        ) : (
          <>
            <p style={{ fontSize: "1em", color: "#333", marginBottom: "1.5em" }}>
              {t('welcome_message', { username: accounts[0].username })}
            </p>
            <button
              onClick={handleLogout}
              style={{
                padding: "12px 25px",
                backgroundColor: "#0038a8",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                fontWeight: "bold",
                fontSize: "1.05em",
                marginBottom: "2em",
                cursor: "pointer",
                boxShadow: "0 4px 8px rgba(0, 56, 168, 0.2)",
                transition: "background-color 0.3s ease, box-shadow 0.3s ease, transform 0.2s ease"
              }}>
              {t('logout_button')}
            </button>

            <UploadComponent userEmail={accounts[0].username} />
          </>
        )}
      </div>
    </div>
  );
}

export default App;